export enum LogLevel {
  Debug = 'Debug',
  Info = 'Info',
  Warn = 'Warn',
  Error = 'Error',
}

export interface LogMessage {
  level: LogLevel;
  message: string;
  timestamp: number;
  category?: string;
  id: string;
}

export interface LogFilter {
  levels?: LogLevel[];
  categories?: string[];
  searchText?: string;
  startTime?: number;
  endTime?: number;
}

export interface LoggerConfig {
  showInConsole: boolean;
  showInUI: boolean;
  maxMessages: number;
  enabledLevels: LogLevel[];
  enabledCategories: string[];
}

export type LogCallback = (logMessage: LogMessage) => void;

/**
 * A singleton Logger class that allows subscribing to log messages.
 * Provides a clean, globally accessible logging interface.
 * Usage: //Logger.Instance.Debug("My debug message");
 */
export class Logger {
  private static _instance: Logger;
  private subscribers: LogCallback[] = [];
  private messages: LogMessage[] = [];
  private config: LoggerConfig = {
    showInConsole: true,
    showInUI: true,
    maxMessages: 1000,
    enabledLevels: [LogLevel.Debug, LogLevel.Info, LogLevel.Warn, LogLevel.Error],
    enabledCategories: []
  };

  private constructor() {
    // Private constructor ensures singleton pattern.
  }

  /**
   * Gets the single instance of the Logger.
   */
  public static get Instance(): Logger {
    if (!Logger._instance) {
      Logger._instance = new Logger();
    }
    return Logger._instance;
  }

  /**
   * Subscribes a callback function to log messages.
   * @param callback The function to call when a new message is logged.
   * @returns An unsubscribe function to remove the subscription.
   */
  public subscribe(callback: LogCallback): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  /**
   * Configure the logger behavior
   */
  public configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  public getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Get all messages with optional filtering
   */
  public getMessages(filter?: LogFilter): LogMessage[] {
    let filtered = [...this.messages];

    if (filter?.levels) {
      filtered = filtered.filter(msg => filter.levels!.includes(msg.level));
    }

    if (filter?.categories) {
      filtered = filtered.filter(msg => 
        msg.category && filter.categories!.includes(msg.category)
      );
    }

    if (filter?.searchText) {
      const search = filter.searchText.toLowerCase();
      filtered = filtered.filter(msg => 
        msg.message.toLowerCase().includes(search) ||
        (msg.category && msg.category.toLowerCase().includes(search))
      );
    }

    if (filter?.startTime) {
      filtered = filtered.filter(msg => msg.timestamp >= filter.startTime!);
    }

    if (filter?.endTime) {
      filtered = filtered.filter(msg => msg.timestamp <= filter.endTime!);
    }

    return filtered;
  }

  /**
   * Clear all messages
   */
  public clearMessages(): void {
    this.messages = [];
  }

  /**
   * Get available categories
   */
  public getAvailableCategories(): string[] {
    const categories = new Set<string>();
    this.messages.forEach(msg => {
      if (msg.category) categories.add(msg.category);
    });
    return Array.from(categories).sort();
  }

  private log(level: LogLevel, message: string, category?: string): void {
    // Check if level is enabled
    if (!this.config.enabledLevels.includes(level)) {
      return;
    }

    // Check if category is enabled (if categories filter is active)
    if (this.config.enabledCategories.length > 0 && category && 
        !this.config.enabledCategories.includes(category)) {
      return;
    }

    const logMessage: LogMessage = {
      level,
      message,
      timestamp: Date.now(),
      category,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    // Store message
    this.messages.push(logMessage);
    
    // Limit stored messages
    if (this.messages.length > this.config.maxMessages) {
      this.messages = this.messages.slice(-this.config.maxMessages);
    }

    // Log to console if enabled
    if (this.config.showInConsole) {
      const categoryPrefix = category ? `[${category}] ` : '';
      switch (level) {
          case LogLevel.Debug: console.debug(`[${level}] ${categoryPrefix}${message}`); break;
          case LogLevel.Info: console.info(`[${level}] ${categoryPrefix}${message}`); break;
          case LogLevel.Warn: console.warn(`[${level}] ${categoryPrefix}${message}`); break;
          case LogLevel.Error: console.error(`[${level}] ${categoryPrefix}${message}`); break;
      }
    }

    // Route to remote log-api if enabled. Routes ALL levels (Debug/Info/Warn/Error).
    // This is the central point — plain console.log stays local, Logger.Instance.* goes remote.
    if (this.remoteSessionId && this.remoteBaseUrl) {
      const logTypeMap: Record<string, string> = { Debug: 'Log', Info: 'Log', Warn: 'Warning', Error: 'Error' };
      const remoteType = logTypeMap[level] || 'Log';
      const categoryPrefix = category ? `[${category}] ` : '';
      this.remoteBuffer.push({
        log_type: remoteType,
        message: `${categoryPrefix}${message}`,
        source: category || 'engine',
        timestamp: new Date().toISOString()
      });
      this.scheduleFlush();
    }

    // Notify UI subscribers if enabled
    if (this.config.showInUI) {
      this.subscribers.forEach(callback => callback(logMessage));
    }
  }

  public Debug(message: string, category?: string): void {
    this.log(LogLevel.Debug, message, category);
  }

  public Info(message: string, category?: string): void {
    this.log(LogLevel.Info, message, category);
  }

  public Warn(message: string, category?: string): void {
    this.log(LogLevel.Warn, message, category);
  }

  public Error(message: string, category?: string): void {
    this.log(LogLevel.Error, message, category);
  }

  // ===== Remote Log-API Transport =====

  private remoteSessionId: string | null = null;
  private remoteBaseUrl: string | null = null;
  private remoteBuffer: Array<{ log_type: string; message: string; source: string; timestamp: string }> = [];
  private remoteFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private remoteEnabled = false;
  // Single-flight promise — guarantees only ONE session is created even if enableRemoteLogging
  // is invoked multiple times concurrently (React StrictMode, multiple components, etc.).
  private _enablingPromise: Promise<string | null> | null = null;

  /**
   * Enable remote logging to the Log-API.
   * Creates a session and flushes buffered logs every 2 seconds.
   * Idempotent: returns the existing session ID on repeated calls.
   */
  public async enableRemoteLogging(baseUrl = 'https://log-api.arkturian.com', appName = '3DPresenter'): Promise<string | null> {
    if (this.remoteEnabled && this.remoteSessionId) {
      return this.remoteSessionId;
    }
    if (this._enablingPromise) {
      return this._enablingPromise;
    }
    this._enablingPromise = this._enableRemoteLoggingImpl(baseUrl, appName);
    return this._enablingPromise;
  }

  private async _enableRemoteLoggingImpl(baseUrl: string, appName: string): Promise<string | null> {
    this.remoteBaseUrl = baseUrl.replace(/\/$/, '');
    try {
      const sessionId = `3dp-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      const res = await fetch(`${this.remoteBaseUrl}/api/v1/sessions`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          app_name: appName,
          device_info: navigator.userAgent.substring(0, 200),
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.remoteSessionId = data.id || sessionId;
      this.remoteEnabled = true;

      // NOTE: We do NOT subscribe here for remote forwarding. The log() method already
      // pushes directly to remoteBuffer when this.remoteSessionId is set (central routing).
      // Adding a subscriber here would double-send every log.

      // Intercept native console.warn/error for 3rd-party libs (catches things like
      // React warnings, Three.js errors that don't go through Logger.Instance)
      this.interceptConsole();

      this.Info(`Remote logging enabled. Session: ${this.remoteSessionId}`, 'Logger');
      return this.remoteSessionId;
    } catch (e) {
      console.warn('Failed to enable remote logging:', e);
      return null;
    }
  }

  public getRemoteSessionId(): string | null {
    return this.remoteSessionId;
  }

  private scheduleFlush(): void {
    if (this.remoteFlushTimer) return;
    this.remoteFlushTimer = setTimeout(() => this.flushRemote(), 500);
  }

  /** Force immediate flush (useful for mobile where timers get throttled) */
  public forceFlush(): void {
    if (this.remoteFlushTimer) { clearTimeout(this.remoteFlushTimer); this.remoteFlushTimer = null; }
    this.flushRemoteSync();
  }

  /** Synchronous flush using sendBeacon (survives page close) */
  private flushRemoteSync(): void {
    if (!this.remoteSessionId || !this.remoteBaseUrl || this.remoteBuffer.length === 0) return;
    const batch = this.remoteBuffer.splice(0, 50);
    const url = `${this.remoteBaseUrl}/api/v1/sessions/${this.remoteSessionId}/logs`;
    const payload = JSON.stringify({ entries: batch });
    try {
      // sendBeacon auto-sends cookies on same-site subdomains, which conflicts with
      // wildcard Access-Control-Allow-Origin on log-api. Use fetch with credentials:'omit'.
      fetch(url, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
    } catch {}
    if (this.remoteBuffer.length > 0) this.scheduleFlush();
  }

  private async flushRemote(): Promise<void> {
    this.remoteFlushTimer = null;
    this.flushRemoteSync();
  }

  private consoleIntercepted = false;
  private interceptConsole(): void {
    if (this.consoleIntercepted) return;
    this.consoleIntercepted = true;
    const origWarn = console.warn;
    const origError = console.error;
    const self = this;

    // Safe stringify: handles circular references (POI/Transform have _parent <-> _children)
    const safeStringify = (a: any): string => {
      if (typeof a === 'string') return a;
      if (a === null || a === undefined) return String(a);
      try {
        const seen = new WeakSet();
        return JSON.stringify(a, (_k, v) => {
          if (typeof v === 'object' && v !== null) {
            if (seen.has(v)) return '[Circular]';
            seen.add(v);
          }
          return v;
        }, 0)?.substring(0, 300) || String(a);
      } catch {
        return String(a);
      }
    };

    // Only intercept console.warn/error (for 3rd-party libs throwing warnings).
    // For our own logs, use Logger.Instance.Info/Debug/Warn/Error which routes to remote directly.
    // We deliberately don't intercept console.log — that stays purely local for performance and clarity.
    console.warn = function (...args: any[]) {
      origWarn.apply(console, args);
      try {
        const msg = args.map(safeStringify).join(' ');
        if (!msg.includes('[Warn]') && !msg.includes('[Error]')) {
          self.remoteBuffer.push({ log_type: 'Warning', message: msg, source: 'console', timestamp: new Date().toISOString() });
          self.scheduleFlush();
        }
      } catch {}
    };
    console.error = function (...args: any[]) {
      origError.apply(console, args);
      try {
        const msg = args.map(safeStringify).join(' ');
        if (!msg.includes('[Error]')) {
          self.remoteBuffer.push({ log_type: 'Error', message: msg, source: 'console', timestamp: new Date().toISOString() });
          self.scheduleFlush();
        }
      } catch {}
    };

    // Uncaught errors
    window.addEventListener('error', (e) => {
      self.remoteBuffer.push({ log_type: 'Exception', message: `${e.message} at ${e.filename}:${e.lineno}`, source: 'window', timestamp: new Date().toISOString() });
      self.scheduleFlush();
    });
    window.addEventListener('unhandledrejection', (e) => {
      self.remoteBuffer.push({ log_type: 'Exception', message: `UnhandledRejection: ${e.reason}`, source: 'promise', timestamp: new Date().toISOString() });
      self.scheduleFlush();
    });
  }
} 